import {
  Alert,
  FieldLabel,
  FormProvider,
  FormSelect,
  FormStreetAddressFieldsAutocomplete,
  FormTextField,
  Grid,
  Skeleton,
  VerticalBox,
  createCountryCodeOptions,
  getTextFieldStyles,
  useCountryStates,
} from "@inductiveautomation/ws-components";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {
  StripeCardElementOptions,
  StripeCardNumberElement,
} from "@stripe/stripe-js";
import { StripeElementChangeEvent } from "@stripe/stripe-js/dist/stripe-js/elements/base";
import { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useGetStateListQuery } from "../../../../contexts/endpoints/shared/stateList.api";
import { useAddressAutocomplete } from "../../../../hooks/useAddressAutocomplete.ts";
import {
  getCreditCardStepStyles,
  getDialogStyles,
} from "../../_training.styles.ts";
import {
  BillingAddressForm,
  CertificationType,
  CreditCardTokenForm,
} from "../../Training.types.ts";
import { updateBillingAddressFormValues } from "./utils.ts";

const CreditCardPaymentForm = ({
  creditCardFormMethods,
  billingAddressFormMethods,
  certificationType,
}: {
  creditCardFormMethods: UseFormReturn<CreditCardTokenForm>;
  billingAddressFormMethods: UseFormReturn<BillingAddressForm>;
  certificationType?: CertificationType;
}) => {
  const { t: translateCountries } = useTranslation("countries");
  const translationKeyPrefix =
    certificationType?.toLowerCase() === "gold"
      ? "training.actions.purchase_test.dialog.gold.steps.credit_card"
      : "training.actions.purchase_test.dialog.core.steps.credit_card";
  const { t } = useTranslation("translation", {
    keyPrefix: translationKeyPrefix,
  });

  const { classes } = getDialogStyles();
  const { classes: creditCardClasses } = getCreditCardStepStyles();
  const countryCodeOptions = createCountryCodeOptions(translateCountries);
  const [selectedCountryId, setSelectedCountryId] = useState("");

  const { data: rawStateList } = useGetStateListQuery(
    {
      countryCode: selectedCountryId,
    },
    { skip: !selectedCountryId }
  );

  useEffect(() => {
    const { country } = billingAddressFormMethods.getValues();
    if (country) {
      const selectedCountry = countryCodeOptions.find(
        ({ value }) => value === country
      );
      setSelectedCountryId(selectedCountry?.value || "");
    }
  }, [billingAddressFormMethods, countryCodeOptions]);

  const { countryHasState, stateList } = useCountryStates(rawStateList?.states);

  const stripe = useStripe();
  const elements = useElements();
  const [fieldStatus, setFieldStatus] = useState({
    cardNumber: { error: null, complete: false },
    cardExpiry: { error: null, complete: false },
    cardCvc: { error: null, complete: false },
  } as Record<string, { error: string | null; complete: boolean }>);
  const [stripeReady, setStripeReady] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState(
    undefined as string | undefined
  );

  const textFieldClasses = getTextFieldStyles({
    isReadOnly: false,
    showBorder: true,
  }).classes;

  const stripeElementOptions: StripeCardElementOptions = {
    disableLink: true,
    classes: {
      base: `${textFieldClasses.doubledBorderStripeContainer} ${textFieldClasses.baseStates} ${textFieldClasses.inputContainer}`,
      empty: textFieldClasses.isPlaceholder,
      focus: textFieldClasses.isActive,
      invalid: textFieldClasses.errorBorder,
    },
    style: {
      // copied from components/src/components/inputs/TextField/_textField.styles.tsx
      base: {
        color: "#464646", // neutral 80
        fontFamily: "Proxima Nova, Arial, sans-serif",
        fontSize: "16px",
        fontWeight: "400",
        fontSmoothing: "antialiased",
        lineHeight: "1.5",
        // padding: "0",

        "::placeholder": {
          color: "#bdbdbd", // neutral 50
        },
      },
    },
  };

  useEffect(() => {
    billingAddressFormMethods.setValue("countryHasState", !!countryHasState);
    void billingAddressFormMethods.trigger("countryHasState");
  }, [countryHasState, billingAddressFormMethods]);

  const {
    suggestions,
    details,
    onLookupAddress,
    onClearSuggestions,
    onAddressSelected,
  } = useAddressAutocomplete();

  useEffect(() => {
    if (details.data) {
      updateBillingAddressFormValues(billingAddressFormMethods, {
        streetAddressLine1: details.data.line1,
        streetAddressLine2: details.data.line2,
        country: details.data.country,
        zipcodeOrPostalCode: details.data.zip,
        city: details.data.city,
        stateOrProvince: details.data.state,
      });
      setSelectedCountryId(details.data.country);
    }
  }, [details.data, billingAddressFormMethods]);

  const requestStripeToken = useCallback(async () => {
    const billingName = creditCardFormMethods.getValues("billingName");
    const {
      streetAddressLine1,
      streetAddressLine2,
      city,
      stateOrProvince,
      country,
      zipcodeOrPostalCode,
    } = billingAddressFormMethods.getValues();

    const allBillingAddressFieldsFilled =
      !!streetAddressLine1 &&
      !!city &&
      !!country &&
      (!countryHasState || !!stateOrProvince) &&
      !!zipcodeOrPostalCode;

    if (
      elements &&
      stripe &&
      fieldStatus.cardNumber.complete &&
      fieldStatus.cardExpiry.complete &&
      fieldStatus.cardCvc.complete &&
      billingName &&
      allBillingAddressFieldsFilled
    ) {
      const stripeCardNumberElement = elements.getElement(
        CardNumberElement
      ) as StripeCardNumberElement;
      const { error: tokenError, token } = await stripe.createToken(
        stripeCardNumberElement,
        {
          name: billingName,
          address_line1: streetAddressLine1,
          address_line2: streetAddressLine2,
          address_city: city,
          address_state: stateOrProvince,
          address_zip: zipcodeOrPostalCode,
          address_country: country,
        }
      );
      if (tokenError) {
        setTokenErrorMessage(tokenError.message);
      } else {
        creditCardFormMethods.setValue("stripeToken", token.id);
        await creditCardFormMethods.trigger("stripeToken");
        if (token.card) {
          creditCardFormMethods.setValue("last4", token.card.last4);
          await creditCardFormMethods.trigger("last4");
        }
      }
    }
  }, [
    billingAddressFormMethods,
    countryHasState,
    creditCardFormMethods,
    elements,
    fieldStatus.cardCvc.complete,
    fieldStatus.cardExpiry.complete,
    fieldStatus.cardNumber.complete,
    stripe,
  ]);

  const { country, stateOrProvince } = billingAddressFormMethods.getValues();
  useEffect(() => {
    void requestStripeToken();
  }, [country, stateOrProvince, requestStripeToken]);

  const resetCCData = async () => {
    setTokenErrorMessage(undefined);
    creditCardFormMethods.setValue("stripeToken", "");
    creditCardFormMethods.setValue("last4", "");
    await creditCardFormMethods.trigger("stripeToken");
  };

  const onStripeChange = async (e: StripeElementChangeEvent) => {
    await resetCCData();
    // check field for errors and completion
    const fieldStatusClone = { ...fieldStatus };
    fieldStatusClone[e.elementType].error = e.error ? e.error.message : null;
    fieldStatusClone[e.elementType].complete = e.complete;
    setFieldStatus(fieldStatusClone);
    void requestStripeToken();
  };

  const setCurrentCountryIdFromValue = (value: string | undefined) => {
    billingAddressFormMethods.setValue("stateOrProvince", "");
    const selectedCountry = countryCodeOptions.find(
      (country) => country.value === value
    );
    if (selectedCountry) {
      setSelectedCountryId(selectedCountry.value || "");
    }
  };

  return (
    <div className={creditCardClasses.container}>
      {tokenErrorMessage && (
        <>
          <Alert
            closeButtonEventHandler={() => undefined}
            description={tokenErrorMessage}
            id="errorAlert"
            testId="stripeTokenErrorAlert"
            variant="error"
          />
          <br />
        </>
      )}
      <FormProvider {...creditCardFormMethods}>
        <form
          onBlur={(event) => {
            if (event.target.name) {
              void requestStripeToken();
            }
          }}
        >
          <VerticalBox gap="1.5rem">
            <h1 className={creditCardClasses.h1}>
              {t("form.credit_card_information")}
            </h1>
            <FormTextField
              prefix="purchase-test"
              name="billingName"
              label={t("form.name_on_card")}
              autoFocus
              validateOnChange
            />

            {!stripeReady && <Skeleton variant={"form_field"} count={1} />}
            <div className={!stripeReady ? classes.visuallyHidden : ""}>
              <FieldLabel htmlFor="stripe-card-number">
                {t("form.card_number")}
              </FieldLabel>
              <CardNumberElement
                id="stripe-card-number"
                options={stripeElementOptions}
                onChange={onStripeChange}
                onReady={resetCCData}
              />
              {fieldStatus.cardNumber.error && (
                <div className={textFieldClasses.extraInfo}>
                  <div className={textFieldClasses.errorMessage}>
                    {fieldStatus.cardNumber.error}
                  </div>
                </div>
              )}
            </div>

            <Grid container spacing={2} sx={{ flexWrap: "no-wrap" }}>
              <Grid size={{ md: 6 }} sx={{ flex: "1" }}>
                {!stripeReady && <Skeleton variant={"form_field"} count={1} />}
                <div className={!stripeReady ? classes.visuallyHidden : ""}>
                  <FieldLabel htmlFor="stripe-expiry">
                    {t("form.expiration_date")}
                  </FieldLabel>
                  <CardExpiryElement
                    id="stripe-expiry"
                    options={stripeElementOptions}
                    onChange={onStripeChange}
                    onReady={() => setStripeReady(true)}
                  />
                  {fieldStatus.cardExpiry.error && (
                    <div className={textFieldClasses.extraInfo}>
                      <div className={textFieldClasses.errorMessage}>
                        {fieldStatus.cardExpiry.error}
                      </div>
                    </div>
                  )}
                </div>
              </Grid>

              <Grid size={{ md: 6 }} sx={{ flex: "1" }}>
                {!stripeReady && <Skeleton variant={"form_field"} count={1} />}
                <div className={!stripeReady ? classes.visuallyHidden : ""}>
                  <FieldLabel htmlFor="stripe-card-cvc">
                    {t("form.security_code")}
                  </FieldLabel>
                  <CardCvcElement
                    id="stripe-card-cvc"
                    options={stripeElementOptions}
                    onChange={onStripeChange}
                  />
                  {fieldStatus.cardCvc.error && (
                    <div className={textFieldClasses.extraInfo}>
                      <div className={textFieldClasses.errorMessage}>
                        {fieldStatus.cardCvc.error}
                      </div>
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </VerticalBox>
        </form>
      </FormProvider>
      <FormProvider {...billingAddressFormMethods}>
        <form
          onBlur={(event) => {
            if (event.target.name) {
              void billingAddressFormMethods.trigger(
                event.target.name as keyof BillingAddressForm
              );
              void requestStripeToken();
            }
          }}
        >
          <VerticalBox gap="1.5rem">
            <h1 className={creditCardClasses.h1}>
              {t("form.billing_address")}
            </h1>
            <FormStreetAddressFieldsAutocomplete
              prefix="purchase-test"
              autoFocusFirstField={false}
              onAddressSelected={onAddressSelected}
              onLookupAddress={onLookupAddress}
              onClearSuggestions={onClearSuggestions}
              suggestions={suggestions.items}
            />

            <FormSelect
              prefix="purchase-test"
              name="country"
              label={t("form.country")}
              options={countryCodeOptions}
              onSelect={setCurrentCountryIdFromValue}
            />

            <Grid container spacing={2}>
              <Grid size={{ md: 6 }}>
                <FormTextField
                  prefix="purchase-test"
                  name="zipcodeOrPostalCode"
                  label={t("form.zip_or_postal_code")}
                />
              </Grid>
              <Grid size={{ md: 6 }}>
                <FormTextField
                  prefix="purchase-test"
                  name="city"
                  label={t("form.city")}
                />
              </Grid>
            </Grid>

            <FormSelect
              prefix="purchase-test"
              name="stateOrProvince"
              label={t("form.state_or_province")}
              options={stateList}
              disabled={!countryHasState}
            />
          </VerticalBox>
        </form>
      </FormProvider>
    </div>
  );
};

export default CreditCardPaymentForm;
